import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ProgressPills } from "@/components/ProgressPills";
import { FollowUpStatus } from "@/components/FollowUpStatus";
import { LogOut, Users, ChevronRight, Search, Mail, Phone, RefreshCw } from "lucide-react";

const TABS = [
  { key: "first_home_buyer", label: "First Home Buyers" },
  { key: "home_buyer", label: "Home Buyers" },
  { key: "investor", label: "Investors" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

export default function CrmDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("first_home_buyer");
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    api.crmClients().then((d) => setClients(d.clients || []))
      .catch(() => toast.error("Could not load clients."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const setStatus = (id, st) => {
    const prev = clients.find((c) => c.id === id)?.follow_up_status || "new";
    setClients((cs) => cs.map((c) => (c.id === id ? { ...c, follow_up_status: st } : c)));
    api.crmSetStatus(id, st).catch(() => {
      toast.error("Could not update status.");
      setClients((cs) => cs.map((c) => (c.id === id ? { ...c, follow_up_status: prev } : c)));
    });
  };

  const counts = useMemo(() => {
    const c = { first_home_buyer: 0, home_buyer: 0, investor: 0 };
    clients.forEach((cl) => { if (c[cl.client_type] != null) c[cl.client_type] += 1; });
    return c;
  }, [clients]);

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return clients.filter((c) => c.client_type === tab)
      .filter((c) => !term || `${c.client_name} ${c.contact_email} ${c.contact_mobile}`.toLowerCase().includes(term));
  }, [clients, tab, q]);

  const doLogout = () => { logout(); navigate("/crm/login", { replace: true }); };

  return (
    <div className="min-h-screen bg-surface relative">
      <div className="ambient-glow" />
      <div className="relative max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand"><Users size={13} /> Consultant CRM</div>
            <h1 className="font-serif text-3xl md:text-4xl text-forest mt-1">Your clients</h1>
            <p className="text-mutedText text-sm mt-1">Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="crm-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-line bg-white/5 px-4 py-2.5 text-sm text-forest hover:bg-white/10 transition">
              <RefreshCw size={15} /> Refresh
            </button>
            <button data-testid="crm-logout" onClick={doLogout} className="inline-flex items-center gap-2 rounded-full border border-line bg-white/5 px-4 py-2.5 text-sm text-forest hover:bg-white/10 transition">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-7 flex flex-wrap gap-2" data-testid="crm-tabs">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={"inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border transition " +
                (tab === t.key ? "bg-brand text-white border-brand" : "bg-white/[0.03] border-line text-mutedText hover:text-forest")}>
              {t.label}
              <span className={"rounded-full px-2 py-0.5 text-[11px] " + (tab === t.key ? "bg-white/20" : "bg-white/10")}>{counts[t.key]}</span>
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="mt-5 relative max-w-sm">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mutedText" />
          <input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, mobile"
            className="w-full rounded-full border border-line bg-white/5 pl-10 pr-4 py-2.5 text-sm text-forest placeholder:text-mutedText/60 focus:outline-none focus:ring-2 focus:ring-brand" />
        </div>

        {/* List */}
        <div className="mt-6 space-y-3" data-testid="crm-client-list">
          {loading ? (
            <div className="text-mutedText text-sm py-10 text-center">Loading clients…</div>
          ) : rows.length === 0 ? (
            <div className="text-mutedText text-sm py-10 text-center rounded-2xl border border-line bg-white/[0.02]" data-testid="crm-empty">No clients in this category yet.</div>
          ) : rows.map((c, i) => (
            <motion.div key={c.id} data-testid={`client-row-${c.id}`} role="button" tabIndex={0}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.03 }}
              onClick={() => navigate(`/crm/clients/${c.id}`)}
              className="w-full text-left rounded-2xl border border-line bg-white/[0.03] p-5 hover:border-brand/40 transition group cursor-pointer">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-forest font-medium truncate">{c.client_name || "Unnamed client"}</span>
                    <span className="rounded-full border border-line bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-widest text-mutedText">{c.client_type_label}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-mutedText mt-1.5">
                    {c.contact_email && <span className="inline-flex items-center gap-1"><Mail size={12} /> {c.contact_email}</span>}
                    {c.contact_mobile && <span className="inline-flex items-center gap-1"><Phone size={12} /> {c.contact_mobile}</span>}
                    <span>Consultant: {c.consultant_name}</span>
                    <span>· {timeAgo(c.updated_at)}</span>
                  </div>
                  <div className="mt-3"><ProgressPills progress={c.progress} /></div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <FollowUpStatus value={c.follow_up_status} onChange={(st) => setStatus(c.id, st)} />
                  <span className="text-xs text-mutedText">{c.progress_done}/{c.progress_total} steps</span>
                  {c.journey_sent && <span className="rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 px-2 py-0.5 text-[10px]">Emailed</span>}
                  <ChevronRight size={18} className="text-mutedText group-hover:text-brand transition" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
